import React, { useCallback, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { CartItem } from './CartItem';
import { ShopifyLineItem } from '../../models/ShopifyLineItem.model';
import { Link } from '../../root/components/Link';
import { FreeGiftItem } from './FreeGiftItem';
import { ViewVariant } from '../../models/ShopifyVariant.model';
import { ProductCategory } from '../../enums/ProductCategory.enum';
import { Color } from '../../enums/Color.enum';
import styles from './CartItems.module.scss';
import classNames from 'classnames';
import { abTestState, assetPathFnState } from '../../recoil/atoms';
import { Preorder } from '../../models/Preorder.model';
import { ViewProducts } from '../../models/ViewProduct.model';
import { isDesktopState, productsState } from '../../recoil/selectors';
import { useSharedGetPreorderState } from '../../hooks/actions/usePreorderState';
import { bundleConfiguration } from '../../hooks/bundleConfiguration';
import { BundleCollectionHandle } from '../../enums/CollectionHandle.enum';
import { ItemForCart, useAddToCartAction, useUpdateCartAction } from '../../hooks/actions/useCartState';
import { v4 } from 'uuid';
import { SubContext } from '../../enums/SubContext.enum';
import { prioritizeValue } from '../../sizing-tool/utils/array-helper';

interface Props {
  lineItems?: ShopifyLineItem[];
}

export const CartItems: React.FC<Props> = ({ lineItems: rawLineItems }: Props) => {
  const assetPathFn = useRecoilValue(assetPathFnState);
  const allProducts = useRecoilValue(productsState);
  const abTests = useRecoilValue(abTestState);
  const isDesktop = useRecoilValue(isDesktopState);
  const [hasPromoDiscount, setHasPromoDiscount] = useState<boolean | undefined>();
  const [promoDiscountMessage, setPromoDiscountMessage] = useState<string | undefined>();
  const gift = findGift(allProducts, rawLineItems);
  const preorders = useSharedGetPreorderState();
  const updateCart = useUpdateCartAction();
  const addToCart = useAddToCartAction();
  const findPreorder = (lineItem?: ShopifyLineItem): Preorder =>
    (preorders || []).find((p: Preorder) => p.sku.toLowerCase() === (lineItem?.sku || '').toLowerCase()) || ({} as Preorder);

  useEffect(() => {
    setHasPromoDiscount(!!localStorage.getItem('hl-promo-discount'));
    setPromoDiscountMessage(JSON.parse(localStorage.getItem('hl-promo-discount') || '{"message": ""}').message);
  }, []);

  const showFreeItem = rawLineItems?.some((li) => !allProducts[li.handle].tags.includes('no_intimates_bag'));

  const removeBundle = useCallback(
    (lineItem: ShopifyLineItem) => {
      const lineItemsToRemove = rawLineItems?.filter((li) => li.properties._bundleKey === lineItem.properties._bundleKey);
      const keysToRemove = lineItemsToRemove?.map((li) => li.key);

      if (keysToRemove) {
        updateCart(
          keysToRemove.reduce<Record<string, number>>((acc, curr) => {
            return { ...acc, [curr]: 0 };
          }, {}),
        );
      }
    },
    [rawLineItems, updateCart],
  );

  const incrementBundle = useCallback(
    (lineItem: ShopifyLineItem) => {
      const lineItemsToDuplicate = rawLineItems?.filter((li) => li.properties._bundleKey === lineItem.properties._bundleKey) ?? [];
      if (!lineItemsToDuplicate) return;
      const uuid = v4();

      const itemsForCart: ItemForCart[] = lineItemsToDuplicate.map((li) => {
        const itemForCart: ItemForCart = {
          productHandle: li.handle,
          quantity: li.quantity,
          variant: allProducts[li.handle].variants.find((v) => `${v.id}` === `${li.variant_id}`)!, // Line Items have variant_id as number but variants have id as string
          properties: {
            ...li.properties,
            _bundleKey: uuid,
          },
        };
        return itemForCart;
      });

      addToCart(itemsForCart, SubContext.CartLineItem);
    },
    [addToCart, allProducts, rawLineItems],
  );

  const lineItems = rawLineItems?.reduce<ShopifyLineItem[]>((acc, curr) => {
    if (curr.properties._bundleHandle && bundleConfiguration[curr.properties._bundleHandle as BundleCollectionHandle]) {
      const existingBundleLineItem = acc.find((li) => li.properties._bundleKey === curr.properties._bundleKey);
      if (existingBundleLineItem) {
        const updatedBundleLineItem = {
          ...existingBundleLineItem,
          original_line_price: existingBundleLineItem.original_line_price + curr.original_line_price,
          price: existingBundleLineItem.price + curr.price,
          final_line_price: existingBundleLineItem.final_line_price + curr.final_line_price,
          discounts: [...existingBundleLineItem.discounts, ...curr.discounts],
          variant_options: prioritizeValue([...existingBundleLineItem.variant_options, curr.variant_options[0]], curr.properties.Bundle?.split(' - ')[1] ?? ''),
        };
        return [...acc.filter((li) => li.properties._bundleKey !== existingBundleLineItem.properties._bundleKey), updatedBundleLineItem];
      } else {
        const bundleTitle = bundleConfiguration[curr.properties._bundleHandle as BundleCollectionHandle].title;
        const bundleConfig = bundleConfiguration[curr.properties._bundleHandle as BundleCollectionHandle];
        const selectedSwatch = curr.properties.Bundle?.split(' - ')[1];
        const productColorCombo = bundleConfig.productColorCombos.find((pcc) => pcc.swatchTitle === selectedSwatch);
        const bundleImageUrl = productColorCombo?.imageAssetUrl;

        return [
          ...acc,
          {
            ...curr,
            quantity: 1,
            title: bundleTitle,
            product_title: bundleTitle,
            image: bundleImageUrl ? assetPathFn(bundleImageUrl) : curr.image,
            variant_options: [curr.variant_options[0]],
          },
        ];
      }
    } else {
      return [...acc, curr];
    }
  }, []);

  return (
    <>
      {hasPromoDiscount && (
        <div className={styles.hlRedeemNotification}>
          <div>
            <img alt="discount" src={assetPathFn('info-circle-regular.svg')} />
            {promoDiscountMessage} will be applied at checkout.
          </div>
        </div>
      )}

      <div className={classNames(styles.hlCartItems)}>
        {!!lineItems &&
          lineItems
            .filter(({ properties }) => !properties?.HoneyloveGift)
            .map((item, index) => (
              <CartItem
                lineItem={item}
                key={index}
                preorder={findPreorder(item)}
                decrementOverride={item.properties._bundleHandle ? () => removeBundle(item) : undefined}
                incrementOverride={item.properties._bundleHandle ? () => incrementBundle(item) : undefined}
              />
            ))}
        {!!lineItems && gift && showFreeItem && <FreeGiftItem variant={gift} />}
        {!lineItems ||
          (lineItems.length === 0 && (
            <div className={styles.hlNoItems}>
              <span>Looks like your bag is empty.</span>
              <Link dataTrackLabel="Shop now" dataTrackLocation="Empty cart" href="/products/">
                Shop now
              </Link>
            </div>
          ))}
      </div>
    </>
  );
};

interface GiftVariants {
  blackIntimatesBag?: ViewVariant;
  whiteIntimatesBag?: ViewVariant;
  mochaIntimatesBag?: ViewVariant;
  tapeMeasure?: ViewVariant;
}

const findGift = (allProducts: ViewProducts, lineItems?: ShopifyLineItem[]) => {
  // Match color for one garment
  // If more than one than cream > onyx > mocha
  const _uniqueColorsInCart = uniqueColorsInCart(lineItems ?? []);
  const primaryColorInCart =
    _uniqueColorsInCart.find(
      (c) => c === Color.Sand || c === Color.Astral || c === Color.Navy || c === Color.Pacific || c === Color.Fig || c === Color.Spicy,
    ) ??
    _uniqueColorsInCart.find((c) => c === Color.Vamp || c === Color.Runway || c === Color.Vamp) ??
    _uniqueColorsInCart.find((c) => c === Color.Mocha);
  return colorToGift(allProducts, primaryColorInCart);
};

const getGiftVariants = (allProducts: ViewProducts): GiftVariants => {
  const intimatesBagRawProduct = allProducts['intimates-bag'];

  return {
    blackIntimatesBag: intimatesBagRawProduct?.variants.find((v) => v.sku === 'HLWB-Onyx'),
    whiteIntimatesBag: intimatesBagRawProduct?.variants.find((v) => v.sku === 'HLWB-Cream'),
    mochaIntimatesBag: intimatesBagRawProduct?.variants.find((v) => v.sku === 'HLWB-Mocha'),
  };
};

const uniqueColorsInCart = (lineItems: ShopifyLineItem[]) => {
  const variantColorsInLineItems = lineItems
    .filter((i) => i.product_type !== ProductCategory.AddOns && i.variant_options && i.variant_options?.length > 0)
    .map((i) => i.variant_options[0]);
  return [...Array.from(new Set(variantColorsInLineItems))];
};

const colorToGift = (allProducts: ViewProducts, primaryColorInCart?: string) => {
  const { blackIntimatesBag, whiteIntimatesBag, mochaIntimatesBag } = getGiftVariants(allProducts);

  switch (primaryColorInCart) {
    case Color.Astral:
    case Color.Sand:
    case Color.Navy:
    case Color.ExplorerBlue:
    case Color.Pacific:
    case Color.Fig:
    case Color.RoseTan:
    case Color.StonewashBlue:
    case Color.Spicy:
    case Color.LilyAndSand:
    case Color.Cinnamon:
    case Color.Peony:
    case Color.StormyBlue:
    case Color.Lilac:
    case Color.DeepAqua:
      return whiteIntimatesBag?.available ? whiteIntimatesBag : undefined;
    case Color.Mocha:
      return mochaIntimatesBag?.available ? mochaIntimatesBag : undefined;
    case Color.Runway:
    case Color.Vamp:
    case Color.JetBlack:
    case Color.HaloGrey:
    case Color.Moondust:
    case Color.Charcoal:
    case Color.VampAndFig:
      return blackIntimatesBag?.available ? blackIntimatesBag : undefined;
    default:
      break;
  }
};

export default CartItems;
